import { DataTable, DataTableSelectionChangeParams } from 'primereact/datatable'
import { PmrE2EBulkEcdEditModel } from '../../interfaces/pmr-e2e-bulk-ecd-edit'
import PmrE2EProjectDetailsOrderlineModel from '../../interfaces/pmr-e2e-project-details-orderline-model'
import classes from './PmrBulkEcdEditDialog.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { Column, ColumnHeaderOptions, ColumnProps } from 'primereact/column'
import { format, isValid } from 'date-fns'
import { Dialog } from 'primereact/dialog'
import clsx from 'clsx'
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox'
import { CalendarDateTemplateParams } from 'primereact/calendar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { maxBy } from 'lodash'
import { CalculateECDAlignmentCheckBehavior } from '../../utils/EcdUtils'
import { PmrBulkEditOrderlineEcdRequest } from '../../../../../shared/models/service-models/PmrBulkEditOrderlineEcdRequest.model'
import { PmrBulkEditFlexProjectEcdRequest } from '../../../../../shared/models/service-models/PmrBulkEditFlexProjectEcdRequest.model'
import ProjectService from '../../../../../services/ProjectService'
import { useDispatch } from 'react-redux'
import {
  updateBulkOrderLineEcds,
  updateRefreshProjectId,
} from '../../../../../features/projectManagement/projectManagementSlice'
import useToastr from '../../../../../hooks/useToastr'
import PmrEditableDateInput from '../pmr-editable-date-input/PmrEditableDateInput'
import moment from 'moment'
import { ECD_ALIGN_DYNAMIC } from '../../../../../utils/constants/feature-flag.constants'

interface Props {
  projectId: string
  isOrderlineEcd: boolean
  visible: boolean
  selectedRows: PmrE2EProjectDetailsOrderlineModel[]
  reloadList: () => void
  onHide: () => void
}

const orderLineECDColumns: ColumnProps[] = [
  {
    field: 'newEcd',
    header: 'New Order Line ECD',
  },
  {
    field: 'flexProjectEcd',
    header: () => {
      return (
        <span>
          FLEX Project ECD
          <FontAwesomeIcon
            title={
              'The FLEX Project ECD represents the overarching expected completion date for all order lines within this FLEX project.'
            }
            icon={faInfoCircle}
            style={{ cursor: 'pointer', paddingLeft: '0.5rem' }}
          />
        </span>
      )
    },
  },
]

const flexProjectECDColumns: ColumnProps[] = [
  {
    field: 'flexProjectEcd',
    header: 'Current FLEX Project ECD',
  },
  {
    field: 'newEcd',
    header: 'New FLEX Project ECD',
  },
]

const PmrBulkEcdEditDialog = (props: Props) => {
  const [ecdLines, setEcdLines] = useState<PmrE2EBulkEcdEditModel[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [hasInputFocus, setHasInputFocus] = useState(false)

  //#region Constants
  const showHeaderBulkRow = props.selectedRows.length > 1
  const title = props.isOrderlineEcd ? 'Bulk-Edit Order Line ECDs' : 'Bulk-Edit FLEX Project ECDs'
  const columns = props.isOrderlineEcd ? orderLineECDColumns : flexProjectECDColumns
  const rowClassName = (rowData: PmrE2EBulkEcdEditModel) => {
    return clsx({
      [classes['checkbox-hide']]: !rowData.e2eProjectLineId,
      'p-highlight': rowData.e2eProjectLineId ? rowData.isSelected : '',
    })
  }
  const calendarStartDate = new Date(2020, 0, 1)
  const calendarEndDate = new Date(2030, 11, 31)

  const selectedEcdLines =
    ecdLines?.filter(ecdLine => ecdLine.isSelected && ecdLine.e2eProjectLineId) ?? []

  const isSelectable = (data: PmrE2EBulkEcdEditModel) => data.isSelectable
  const isButtonDisabled = ecdLines.some(item => item.hasError) || isSaving

  const isRowSelectable = (event: any) => (event.data ? isSelectable(event.data) : true)

  const latestOrderBookedDate = new Date(
    maxBy(props.selectedRows, row => new Date(row.flexProjectBookedDate))!.flexProjectBookedDate
  )

  const ecdTooltips = ['Order Line ECD', 'FLEX Project ECD']
  const dispatch = useDispatch()
  const { showSuccess, showError, showToast } = useToastr()
  //#endregion

  //#region Hooks
  const populateDataTable = useCallback(() => {
    if (props.visible) {
      const lines = props.selectedRows.map(selectedRow => {
        let defaultCheckAlignment = false

        if (props.isOrderlineEcd && !selectedRow.flexProjectECD) defaultCheckAlignment = true

        if (!props.isOrderlineEcd && !selectedRow.orderLineECD) defaultCheckAlignment = true

        const transformSelected: PmrE2EBulkEcdEditModel = {
          isSelected: false,
          isSelectable: true,
          e2eProjectLineId: selectedRow.id,
          flexProjectName: selectedRow.flexProjectName ?? '',
          orderLineDescription: selectedRow.orderLineNumber ?? '',
          orderLineEcd: selectedRow.orderLineECD ? new Date(selectedRow.orderLineECD) : null,
          flexProjectEcd: selectedRow.flexProjectECD ? new Date(selectedRow.flexProjectECD) : null,
          isAlign: defaultCheckAlignment,
          isDefaultChecked: defaultCheckAlignment,
          newEcd: undefined,
        }
        return transformSelected
      })

      const bulkHeaderRow: PmrE2EBulkEcdEditModel = {
        isSelected: false,
        isSelectable: false,
        e2eProjectLineId: '',
        flexProjectName: '',
        orderLineDescription: '',
        orderLineEcd: null,
        flexProjectEcd: null,
        isAlign: false,
        isDefaultChecked: false,
        newEcd: undefined,
      }

      const renderLines = showHeaderBulkRow ? [{ ...bulkHeaderRow }, ...lines] : lines

      setEcdLines(renderLines)
    }
  }, [props.visible])

  useEffect(() => {
    populateDataTable()
  }, [populateDataTable])
  //#endregion

  //#region Other Templates
  const dateTemplate = (e: CalendarDateTemplateParams) => {
    const dateToCompare = new Date(e.year, e.month, e.day)
    const isEarlierThanLatestOrderBookedDate =
      latestOrderBookedDate &&
      dateToCompare <
        new Date(
          latestOrderBookedDate.getFullYear(),
          latestOrderBookedDate.getMonth(),
          latestOrderBookedDate.getDate()
        )

    return (
      <div
        title={
          isEarlierThanLatestOrderBookedDate
            ? "Cannot select Dates earlier than the selected Order Lines' Order Booked Date."
            : ''
        }
      >
        <span className={clsx(isEarlierThanLatestOrderBookedDate && classes['not-allowed'])}>
          {e.day}
        </span>
      </div>
    )
  }
  //#endregion

  //#region Inline Components
  const InlineAlignmentHeader = () => {
    const alignmentTooltip = `Tick this checkbox to align the ${
      props.isOrderlineEcd ? ecdTooltips[1] : ecdTooltips[0]
    } with the new ${
      props.isOrderlineEcd ? ecdTooltips[0] : ecdTooltips[1]
    }. If disabled, alignment is required due to system rules.`

    if (props.isOrderlineEcd)
      return (
        <div className={classes['ecd-alignment-header__container']}>
          FLEX Project ECD Alignment
          <FontAwesomeIcon
            title={alignmentTooltip}
            icon={faInfoCircle}
            style={{ cursor: 'pointer', paddingLeft: '0.5rem' }}
          />
        </div>
      )
    else {
      return (
        <div className={classes['ecd-alignment-header__container']}>
          Order Line ECD Alignment
          <FontAwesomeIcon
            title={alignmentTooltip}
            icon={faInfoCircle}
            style={{ cursor: 'pointer', paddingLeft: '0.5rem' }}
          />
        </div>
      )
    }
  }

  const InlineEcdInput = (ecdLines: PmrE2EBulkEcdEditModel) => {
    const id = ecdLines.isSelectable ? `ecd-${ecdLines.e2eProjectLineId}` : undefined
    const orderBookDate = props.selectedRows.find(
      line => line.id === ecdLines.e2eProjectLineId
    )?.flexProjectBookedDate
    const [tempValue, setTempValue] = useState<Date | undefined>(ecdLines.newEcd)
    const [hasError, setHasError] = useState(false)

    return (
      <>
        <div className={classes['new-ecd-container']}>
          <PmrEditableDateInput
            id={id}
            className={classes['new-ecd-calendar']}
            onEditDate={(selectedDate, hasError) => {
              if (typeof selectedDate !== 'string') setTempValue(selectedDate ?? undefined)
              setHasError(hasError)
            }}
            value={tempValue}
            yearRange={`${calendarStartDate.getFullYear()}:${calendarEndDate.getFullYear()}`}
            icon={
              <FontAwesomeIcon
                title="Click to select a date or type in the format ‘'yyyy/mm/dd'’"
                icon={faCalendarAlt}
              />
            }
            showIcon
            orderBookedDate={!!orderBookDate ? moment(orderBookDate).toDate() : undefined}
            hasError={ecdLines.hasError}
            onHide={() => handleBulkECDDateAdjustment(tempValue ?? null, id, hasError)}
            onFocus={() => setHasInputFocus(true)}
          />
        </div>
      </>
    )
  }

  const InlineSavingBody = () => {
    return (
      <div className={classes['loading-div']}>
        <div>Saving ......</div>
        <div>
          <svg className="ul-progressCircle -medium">
            <circle className="ul-progressCircle__outer" cx="50%" cy="50%" r="35%" />
            <circle className="ul-progressCircle__inner" cx="50%" cy="50%" r="35%" />
          </svg>
        </div>
      </div>
    )
  }

  const InlineFooter = () => (
    <>
      <hr className={classes['modal-footer-hr']} />
      <div className={`${classes['dialog-footer']} ${classes['center-items']}`}>
        <button
          className={`${classes['dialog-cancel-btn']} ul-button -medium`}
          disabled={isButtonDisabled}
          onClick={isButtonDisabled ? undefined : handleClose}
        >
          CANCEL
        </button>
        <button
          className={`${classes['dialog-save-btn']} ul-button -medium`}
          disabled={isButtonDisabled}
          onClick={handleSave}
        >
          SAVE
        </button>
      </div>
    </>
  )

  const InlineDialogBody = () => {
    const InlineFunctionInstructionBody = () => {
      if (props.isOrderlineEcd)
        return (
          <div className={classes['instruction-body-content']}>
            <p>
              Updating the Order Line ECD recalibrates all ongoing tasks and milestones schedule. If
              the new Order Line ECD is:
            </p>
            <ul>
              <li>
                <strong>EARLIER THAN the current FLEX Project ECD:</strong> You can choose to align
                the FLEX Project ECD with the Order Line ECD or keep current FLEX Project ECD.
              </li>
              <li>
                <strong>LATER THAN the current FLEX Project ECD:</strong> The FLEX Project ECD will
                automatically update to align with the Order Line ECD.
              </li>
              <li>
                <strong>If there is no FLEX Project ECD:</strong>{' '}
                {ECD_ALIGN_DYNAMIC
                  ? 'You can choose to align the FLEX Project ECD with the Order Line ECD, or keep it blank.'
                  : 'The FLEX Project ECD will default to Order Line ECD.'}
              </li>
              <li style={{ listStyle: 'none', paddingLeft: '1rem' }}>
                <em style={{ color: 'red' }}>
                  * Updating the Order Line will recalibrate all ongoing tasks and milestones
                  schedule.
                </em>
              </li>
            </ul>
          </div>
        )
      else {
        return (
          <div className={classes['instruction-body-content']}>
            <p>If the new FLEX Project is: </p>
            <ul>
              <li>
                <strong>LATER THAN the current Order Line ECD:</strong> You can choose to align the
                Order Line ECD with the FLEX Project ECD or keep current Order Line ECD.
              </li>
              <li>
                <strong>EARLIER THAN the current Order Line ECD:</strong> The Order Line ECD will
                automatically update to align with the FLEX Project ECD.
              </li>
              <li>
                <strong>If there is no Order Line ECD:</strong>{' '}
                {ECD_ALIGN_DYNAMIC
                  ? 'You can choose to align the Order Line ECD with the FLEX Project ECD, or keep it blank.'
                  : 'The Order Line ECD will default to FLEX Project ECD.'}
              </li>
              <li style={{ listStyle: 'none', paddingLeft: '1rem' }}>
                <em style={{ color: 'red' }}>
                  * Updating the Order Line will recalibrate all ongoing tasks and milestones
                  schedule.
                </em>
              </li>
            </ul>
          </div>
        )
      }
    }

    return (
      <div>
        <InlineFunctionInstructionBody />
        <div className={classes['table-container']}>
          <DataTable
            scrollHeight="100rem"
            className={classes['custom-table']}
            value={ecdLines}
            showGridlines
            selection={selectedEcdLines}
            onSelectionChange={handleSelectionChange}
            isDataSelectable={isRowSelectable}
            rowClassName={rowClassName}
            responsiveLayout="scroll"
            scrollable
            size="small"
            columnResizeMode="expand"
          >
            <Column
              alignFrozen="left"
              frozen
              style={{ width: '3%', flex: '0 0 auto' }}
              key="checkbox"
              columnKey="checkbox"
              header={(options: ColumnHeaderOptions) => {
                const data: PmrE2EBulkEcdEditModel[] = options.props.value
                const isAllChecked = data
                  .filter(line => line.isSelectable)
                  .every(line => line.isSelected)

                return (
                  <Checkbox
                    id={'ecd-bulk-edit-header-checkbox'}
                    onChange={handleCheckboxSelection}
                    checked={isAllChecked}
                  />
                )
              }}
              body={data => {
                if (data.e2eProjectLineId) {
                  return (
                    <Checkbox
                      id={data.e2eProjectLineId}
                      onChange={handleCheckboxSelection}
                      checked={data.isSelected}
                    />
                  )
                }
              }}
            ></Column>
            <Column
              field="flexProjectName"
              header="FLEX Project"
              style={{ width: '17%', flex: '0 0 auto' }}
              body={data => {
                return (
                  <span title={data.flexProjectName} className="text-container-ellipsis">
                    {data.flexProjectName}
                  </span>
                )
              }}
              key="flexProjectName"
              columnKey="flexProjectName"
            />

            <Column
              field="orderLineDescription"
              header="Order Line Number"
              style={{ width: '17%', flex: '0 0 auto' }}
              body={data => {
                return (
                  <span title={data.orderLineDescription} className="text-container-ellipsis">
                    {data.orderLineDescription}
                  </span>
                )
              }}
              key="orderLineDescription"
              columnKey="orderLineDescription"
            />
            <Column
              field="orderLineEcd"
              header={
                props.isOrderlineEcd
                  ? 'Current Order Line ECD'
                  : () => {
                      return (
                        <span>
                          Order Line ECD
                          <FontAwesomeIcon
                            title={
                              'The Order Line ECD represents the overarching expected completion date for all tasks within this FLEX project.'
                            }
                            icon={faInfoCircle}
                            style={{ cursor: 'pointer', paddingLeft: '0.5rem' }}
                          />
                        </span>
                      )
                    }
              }
              style={{ width: '17%', flex: '0 0 auto' }}
              body={(data: PmrE2EBulkEcdEditModel) => {
                return data.orderLineEcd ? (
                  <span className="text-container-ellipsis">
                    {format(new Date(data.orderLineEcd), 'dd MMM yyyy')}
                  </span>
                ) : (
                  ''
                )
              }}
              key="orderLineEcd"
              columnKey="orderLineEcd"
            />
            {columns.map((column, index) => {
              return (
                <Column
                  field={column.field}
                  header={column.header}
                  style={{ width: '17%', flex: '0 0 auto' }}
                  key={column.field}
                  columnKey={column.field}
                  body={(data: PmrE2EBulkEcdEditModel) => {
                    if (column.field === 'newEcd') {
                      return <InlineEcdInput {...data} />
                    } else {
                      if (data.isSelectable) {
                        const ecdToDisplay =
                          column.field === 'flexProjectEcd'
                            ? data.flexProjectEcd
                            : data.orderLineEcd
                        const formatDate = ecdToDisplay
                          ? format(new Date(ecdToDisplay), 'dd MMM yyyy')
                          : null
                        return <span>{formatDate}</span>
                      }
                    }
                  }}
                />
              )
            })}
            <Column
              field="flexProjectECDAdjustment"
              header={<InlineAlignmentHeader />}
              style={{ width: '12%', flex: '0 0 auto' }}
              body={(data: PmrE2EBulkEcdEditModel) => {
                return (
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <Checkbox
                      checked={data.isAlign}
                      id={data.e2eProjectLineId}
                      onChange={handleEcdAlignmentChange}
                      disabled={data.isDefaultChecked}
                    />
                  </div>
                )
              }}
              key="flexProjectECDAdjustment"
              columnKey="flexProjectECDAdjustment"
            />
          </DataTable>
        </div>
      </div>
    )
  }
  //#endregion

  //#region Events
  const waitForInputSync = () => {
    if (!hasInputFocus) return

    return new Promise(resolve => {
      setTimeout(() => {
        setHasInputFocus(false)
        resolve(true)
      }, 500)
    })
  }
  const handleSave = async () => {
    await waitForInputSync()
    setIsSaving(true)
    const payload = ecdLines
      .filter(lines => lines.isSelectable)
      .map(validLines => {
        if (props.isOrderlineEcd) {
          const bulkOrderlinePayloadItem: PmrBulkEditOrderlineEcdRequest = {
            e2eProjectLineId: validLines.e2eProjectLineId,
            newEcd: validLines.newEcd ? format(new Date(validLines.newEcd), 'yyyy-MM-dd') : null,
            orderLineEcd: null,
            isAlignOrderlineAndFlexProjectEcd: validLines.isAlign,
          }
          return bulkOrderlinePayloadItem
        } else {
          const bulkOrderlinePayloadItem: PmrBulkEditFlexProjectEcdRequest = {
            e2eProjectLineId: validLines.e2eProjectLineId,
            newEcd: validLines.newEcd ? format(new Date(validLines.newEcd), 'yyyy-MM-dd') : null,
            orderLineEcd: validLines.orderLineEcd
              ? format(new Date(validLines.orderLineEcd), 'yyyy-MM-dd')
              : null,
            isAlignOrderlineAndFlexProjectEcd: validLines.isAlign,
          }
          return bulkOrderlinePayloadItem
        }
      })

    try {
      const service = props.isOrderlineEcd
        ? await ProjectService.bulkUpdateOrderlineEcd(payload)
        : await ProjectService.bulkUpdateFlexProjectEcd(payload)

      if (service.isSuccess) {
        dispatch(updateBulkOrderLineEcds(service.data))
        const successMessage = props.isOrderlineEcd
          ? 'The new Order Line ECD has been successfully saved and schedule recalibrations have been applied to your selected order lines.'
          : 'The new FLEX Project ECD has been successfully saved to your Order Lines.'

        showSuccess(
          `${props.isOrderlineEcd ? ecdTooltips[0] : ecdTooltips[1]}s Updated`,
          successMessage
        )

        dispatch(updateRefreshProjectId(props.projectId))

        resetModalUponClose()
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred. Please try again later.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    showToast(
      'ECD Update Cancelled',
      'No changes were made to the Order Line ECD nor FLEX Project ECD.',
      'info'
    )
    resetModalUponClose()
  }

  const resetModalUponClose = () => {
    setEcdLines([])
    setIsSaving(false)
    props.onHide()
  }

  const handleSelectionChange = (e: DataTableSelectionChangeParams) => {
    setEcdLines(current => {
      const updateSelection = current.map(cur => {
        const updateSelect = e.value?.find(
          (updated: any) => updated.e2eProjectLineId === cur.e2eProjectLineId
        )
        if (updateSelect) {
          updateSelect.isSelected = true
          return { ...cur, ...updateSelect }
        }

        cur.isSelected = false
        return cur
      })

      return updateSelection
    })
  }

  const handleCheckboxSelection = (e: CheckboxChangeParams) => {
    if (!e.target.id) return

    setEcdLines(current => {
      if (e.target.id === 'ecd-bulk-edit-header-checkbox') {
        const updateCurrent = current.map(cur => {
          if (!cur.isSelectable) return cur

          cur.isSelected = e.checked
          return cur
        })
        return updateCurrent
      } else {
        const updateCurrent = current.map(cur => {
          if (!cur.isSelectable) return cur

          if (cur.e2eProjectLineId === e.target.id) {
            cur.isSelected = e.checked
          }
          return cur
        })

        return updateCurrent
      }
    })
  }

  const handleEcdAlignmentChange = (e: CheckboxChangeParams) => {
    if (!e.target.id && selectedEcdLines.length === 0) return

    setEcdLines(current => {
      let updateCurrent = current.map(cur => {
        if (cur.isDefaultChecked) {
          if (e.target.id) {
            cur.isSelected = false
          }
          return cur
        }

        if (!e.target.id) {
          if (cur.isSelected || !cur.isSelectable) cur.isAlign = e.checked

          return cur
        }

        if (e.target.id) {
          cur.isSelected = false

          if (cur.e2eProjectLineId === e.target.id) {
            cur.isSelected = true
            cur.isAlign = e.checked
          }

          return cur
        }

        return cur
      })
      return updateCurrent
    })
  }

  const handleBulkECDDateAdjustment = (
    selectedDate: Date | null | string,
    targetElementId: string | undefined,
    hasError?: boolean
  ) => {
    if (!targetElementId && selectedEcdLines.length === 0 && !hasError) return

    const newEcd = (selectedDate as Date) ?? undefined

    setEcdLines(current => {
      let updateCurrent = current.map(cur => {
        if (!targetElementId) {
          if ((cur.isSelected || !cur.isSelectable) && !hasError) cur.newEcd = newEcd

          return cur
        }

        if (targetElementId) {
          cur.isSelected = false
          const elementId = `ecd-${cur.e2eProjectLineId}`
          if (targetElementId === elementId) {
            cur.isSelected = true
            if (!hasError) cur.newEcd = newEcd
          }
          return cur
        }
        return cur
      })

      if (!targetElementId) {
        updateCurrent = updateCurrent.map(updatedCurr => {
          if (!updatedCurr.isSelectable) return updatedCurr

          updatedCurr = updateAlignmentCheck(updatedCurr)
          return updatedCurr
        })
      } else {
        updateCurrent = updateCurrent.map(updatedCurr => {
          if (!updatedCurr.isSelectable) return updatedCurr

          const elementId = `ecd-${updatedCurr.e2eProjectLineId}`
          if (targetElementId === elementId) {
            updatedCurr = updateAlignmentCheck(updatedCurr)
          }

          return updatedCurr
        })
      }

      return updateCurrent
    })

    setHasInputFocus(false)
  }

  //#endregion

  //#region Misc
  const updateAlignmentCheck = (updatedCurr: PmrE2EBulkEcdEditModel) => {
    const orderLineEcdToCheck = props.isOrderlineEcd ? updatedCurr.newEcd : updatedCurr.orderLineEcd

    const flexProjectEcdToCheck = props.isOrderlineEcd
      ? updatedCurr.flexProjectEcd
      : updatedCurr.newEcd

    let calculateCheck = CalculateECDAlignmentCheckBehavior(
      props.isOrderlineEcd,
      orderLineEcdToCheck,
      flexProjectEcdToCheck
    )

    updatedCurr.isAlign =
      updatedCurr.isDefaultChecked !== calculateCheck ? calculateCheck : updatedCurr.isAlign
    updatedCurr.isDefaultChecked = calculateCheck

    return updatedCurr
  }

  //#endregion

  return (
    <Dialog
      className={classes['custom-dialog']}
      header={title}
      footer={<InlineFooter />}
      draggable={false}
      closeOnEscape={false}
      visible={props.visible}
      onHide={handleClose}
      style={{ width: '75%', maxWidth: '75%', overflowY: 'auto' }}
    >
      <hr className="modal-header-hr" />
      <div className="body-container">
        {!isSaving && <InlineDialogBody />}
        {isSaving && <InlineSavingBody />}
      </div>
    </Dialog>
  )
}

export default PmrBulkEcdEditDialog
